'use client'
import { Button } from '@/components/Button'
import { Loading } from '@/components/Loading'
import { Popup } from '@/components/Popup'
import { SearchInput } from '@/components/SearchInput'
import { Switch } from '@/components/Switch'
import { Icon } from '@/icons'
import { SEARCH_STRING_MIN_LENGTH } from '@/models/Mktu'
import { SelectWordsStore } from '@/domains/Account/modules/Mktu/store/SelectWords.store'
import { MktuClassWord } from '@/services/Api/generated'
import { NiceSwitch, SelectedWordsList } from '@/shared/Mktu/components'
import { niceSwitch } from '@/store/NiceSwitch'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import Plural from 'plural-ru'
import { FunctionComponent, useCallback, useEffect } from 'react'
import { WordOption } from './components/WordOption'
import styles from './SelectWordsPopup.module.scss'

interface SelectWordPopupProps {
  className?: string
  store: SelectWordsStore
  onClose?: () => void
  onApply?: (words: MktuClassWord[]) => void
  onShowAllList: () => void
}
export const SelectWordsPopup: FunctionComponent<SelectWordPopupProps> =
  observer(({ className, store, onClose, onApply, onShowAllList }) => {
    const apply = useCallback(() => {
      onApply(store.selectedWords)
    }, [onApply, store])

    const deleteSelectedWord = useCallback(
      (wordName: MktuClassWord['word']) => {
        const word = store.selectedWords.find((word) => word.word === wordName)

        if (word != null) {
          store.setSelectedWords(word)
        }
      },
      [store]
    )

    useEffect(() => {
      return () => {
        niceSwitch.setVisibleAttention(false)
      }
    }, [])

    const switchNiceFromError = useCallback(
      (value: boolean) => {
        if (!niceSwitch.attentionIsRead) {
          niceSwitch.setVisibleAttention(true)
        }
        store.setOnlyNice(value)
      },
      [store]
    )

    return (
      <Popup className={cn(className, styles.popup)} onClose={onClose}>
        <div className={styles.popupContainer}>
          <div className={styles.popupHeader}>
            <div className={styles.title}>
              Введите название товара или услуги для подбора классов
            </div>
            <NiceSwitch onChange={store.setOnlyNice} value={store.onlyNice} />
            <button className={styles.buttonClose} onClick={onClose}>
              <Icon name="close" className={styles.iconClose} />
            </button>
          </div>
          <div className={styles.search}>
            <SearchInput
              className={styles.searchInput}
              onChange={store.setSearchString}
              value={store.searchString}
              placeholder="Например: одежда"
              autocomplete={false}
              autoFocus
            />
            <div className={cn(styles.mktuNumberTitle, styles.isShowXs)}>
              Найдено {store.suggestedWordsFilterByClasses.length}{' '}
              {Plural(
                store.suggestedWordsFilterByClasses.length,
                'термин',
                'термина',
                'терминов'
              )}{' '}
              в классах:
            </div>
            <div
              className={cn(styles.mktuNumberList, {
                [styles.isLoading]: store.suggestedWordsLoader.loading,
                [styles.isVisible]:
                  store.suggestedWordsFilterByClasses.length !== 0,
              })}
            >
              <div className={cn(styles.mktuNumberTitle, styles.isHideXs)}>
                Найдено {store.suggestedWordsFilterByClasses.length}{' '}
                {Plural(
                  store.suggestedWordsFilterByClasses.length,
                  'термин',
                  'термина',
                  'терминов'
                )}{' '}
                в классах:
              </div>
              {store.mktuNumbersList.map((number) => (
                <MktuClassNumnberButton
                  key={number}
                  number={number}
                  onClick={store.setFilterClasses}
                  isSelected={store.filterClasses.includes(number)}
                />
              ))}
            </div>

            <SelectedWordsList
              listClassName={styles.selectedWordsList}
              isHorizontalOnMobile
              words={store.selectedWords}
              onDelete={deleteSelectedWord}
              onShowAll={onShowAllList}
            />
          </div>
          {store.suggestedWordsLoader.loading ? (
            <Loading className={styles.loader} withText={false} />
          ) : null}
          <div
            className={cn(styles.popupContent, {
              [styles.isLoading]: store.suggestedWordsLoader.loading,
              [styles.withoutBorder]: store.suggestedWords.length === 0,
            })}
          >
            {store.searchString.length < SEARCH_STRING_MIN_LENGTH &&
            store.suggestedWords.length === 0 &&
            !store.suggestedWordsLoader.loading ? (
              <div className={styles.emptyList}>
                Здесь будут отображаться термины, необходимые для подбора
                классов МКТУ
              </div>
            ) : null}

            {store.searchString.length >= SEARCH_STRING_MIN_LENGTH &&
            store.suggestedWords.length === 0 &&
            store.onlyNice &&
            !store.suggestedWordsLoader.loading ? (
              <div className={cn(styles.emptyList, styles.isDark)}>
                <div>
                  Ничего не найдено. Попробуйте изменить запрос или выключите
                  опцию Только НИЦЦА{' '}
                  <Switch
                    checked={store.onlyNice}
                    onChange={switchNiceFromError}
                    className={styles.switchNice}
                  />
                </div>
              </div>
            ) : null}

            {store.searchString.length >= SEARCH_STRING_MIN_LENGTH &&
            store.suggestedWords.length === 0 &&
            !store.onlyNice &&
            !store.suggestedWordsLoader.loading ? (
              <div className={cn(styles.emptyList, styles.isDark)}>
                <div>Ничего не найдено. Попробуйте изменить запрос </div>
              </div>
            ) : null}

            {store.suggestedWordsFilterByClasses.length !== 0 ? (
              <>
                <div className={styles.list}>
                  {store.suggestedWordsFilterByClasses.map((word) => (
                    <WordOption
                      key={word.uuid}
                      word={word}
                      isSelected={
                        store.selectedWords.find((w) => word.uuid === w.uuid) !=
                        null
                      }
                      onSelect={store.setSelectedWords}
                    />
                  ))}
                </div>
              </>
            ) : null}
          </div>
          <div className={styles.popupFooter}>
            <Button
              variant="secondary"
              disabled={store.selectedWords.length === 0}
              onClick={store.resetAll}
            >
              <Icon
                name="trash"
                className={cn(styles.resetAllIcon, {
                  [styles.isDisabled]: store.selectedWords.length === 0,
                })}
              />{' '}
              Сбросить все
            </Button>
            {store.selectedWords.length !== 0 ? (
              <span className={styles.selectedCount}>
                Выбрано позиций: {store.selectedWords.length}
              </span>
            ) : null}
            <Button onClick={apply} disabled={store.selectedWords.length === 0}>
              Применить
            </Button>
          </div>
        </div>
      </Popup>
    )
  })

interface MktuClassNumberButtonProps {
  onClick: (classNumber: MktuClassWord['classNumber']) => void
  number: MktuClassWord['classNumber']
  isSelected: boolean
}
const MktuClassNumnberButton: FunctionComponent<MktuClassNumberButtonProps> = ({
  onClick,
  number,
  isSelected,
}) => {
  const click = useCallback(() => {
    onClick(number)
  }, [onClick, number])

  return (
    <button
      key={number}
      className={cn(styles.mktuNumberButton, {
        [styles.isSelected]: isSelected,
      })}
      onClick={click}
    >
      {number}
    </button>
  )
}
